<aix-templates #aixTemplates (onInit)="aixTemplatesInitialized = true"></aix-templates>

<ng-template #signaturesStepContent>
    <div class="order-step-content">
        @if (bundleOutcomeUnknown || bundlePartialWarning) {
        <!-- Bundle send outcome notice — rendered OUTSIDE the bundle view so it survives the post-send
             refresh that recomputes viewState (otherwise the "do not resend" warning would vanish). -->
        <div class="u-mb16" data-testing="bundle-outcome-notice">
            @if (bundleOutcomeUnknown) {
            <p class="u-mb8 ph-unmask" data-testing="bundle-outcome-unknown">
                We couldn't confirm the result of your send. Do not resend — check these orders'
                status; any envelopes DocuSign accepted are being reconciled.
            </p>
            } @if (bundlePartialWarning) {
            <p class="u-mb8" data-testing="bundle-partial-warning">{{ bundlePartialWarning }}</p>
            }
            <aix-button
                [data-testing]="'dismiss-bundle-outcome'"
                (click)="dismissBundleOutcome()"
                [buttonType]="linkButtonType"
                [buttonLabel]="'Dismiss'"
            >
            </aix-button>
        </div>
        } @if (filesGenerated && viewState !== 'map-signatures') {
        <aix-pdf-generation
            [order]="order"
            [viewState]="viewState"
            [signaturesCompleted]="signaturesCompleted"
            [wetSignOnly]="wetSignOnly"
            [bundleOrders]="bundlePreviewOrders"
            (onMakeChanges)="makeChanges()"
            (onGenerateFiles)="filesGenerated = true"
        >
        </aix-pdf-generation>
        } @if (viewState === 'wet-sign' && order?.status === orderStatus.readyToSend) {
        <div>
            <aix-header-section
                [title]="'Download & Sign'"
                [data-testing]="'download-and-sign-title'"
            >
                @if (!wetSignOnly) {
                <aix-button
                    [data-testing]="'e-sign-button'"
                    [isDisabled]="isReadOnly"
                    (click)="onClickESign()"
                    [buttonType]="linkButtonType"
                    [buttonLabel]="'I\'d Rather eSign'"
                >
                </aix-button>
                }
            </aix-header-section>
        </div>
        } @if (viewState === 'wet-sign' && order?.status === orderStatus.readyToSend) {
        <aix-form-upload #orderFormUpload [isReadOnly]="isReadOnly"></aix-form-upload>
        } @if (viewState === 'preview' && order?.status === orderStatus.readyToSend) {
        <aix-delivery
            [order]="order"
            [isReadOnly]="isReadOnly"
            [wetSignOnly]="wetSignOnly"
            [hasBundleCandidates]="hasBundleCandidates"
            (onMakeChanges)="makeChanges()"
            (clickContinue)="onSelectSigningMethod($event)"
        >
        </aix-delivery>
        } @if (viewState === 'map-signatures' && order?.status === orderStatus.readyToSend) {
        <aix-map-signatures
            (submitForm)="onSubmitMapSignatures()"
            (cancelMapSignatures)="clearSigningMethod()"
        >
        </aix-map-signatures>
        } @if (viewState === 'docu-sign' && order?.status === orderStatus.readyToSend) {
        <aix-docu-sign
            [signers]="order?.eSign?.envelope?.recipients || []"
            [states]="states"
            [recipientError]="recipientError"
            (cancelESign)="clearSigningMethod()"
            (submitForm)="onSubmitDocuSign($event)"
        >
        </aix-docu-sign>
        } @if (viewState === 'bundle' && order?.status === orderStatus.readyToSend) {
        <div class="u-mt24" data-testing="bundle-signing">
            <h5 class="ph-unmask">Bundle with other orders</h5>
            @if (multiProduct) {
            <p class="u-mt8 u-mb8 ph-unmask">
                These orders span more than one product — confirm they belong in one package.
            </p>
            }
            <p class="u-mt8 u-mb16 ph-unmask">
                Select the other orders to sign in one DocuSign package. The investor receives one
                email and signs all selected orders together.
            </p>
            @for (candidate of bundleCandidates; track candidate.id) {
            <!-- Order numbers and product names only. -->
            <label class="u-db u-mb8 ph-unmask">
                <input
                    type="checkbox"
                    [checked]="selectedCandidateIds.has(candidate.id)"
                    (change)="toggleBundleCandidate(candidate.id)"
                    [attr.data-testing]="'bundle-candidate-' + candidate.id"
                />
                {{ candidate.orderNumber }}@if (candidate.fund?.name) { — {{ candidate.fund?.name }}
                }
            </label>
            } @if (bundleError) {
            <!-- Bundle-specific error (empty selection / fail-closed rejection). NOT routed through the
                 shared aix-docu-sign recipientError, which is reserved for invalid-email responses and
                 would otherwise mislabel a bundle rejection as "undeliverable email addresses". -->
            <p class="aix-error u-mt8 u-mb8" data-testing="bundle-error">{{ bundleError }}</p>
            }
            <aix-docu-sign
                [signers]="order?.eSign?.envelope?.recipients || []"
                [states]="bundleStates"
                [recipientError]="''"
                (cancelESign)="onCancelBundle()"
                (submitForm)="onSubmitBundle($event)"
            >
            </aix-docu-sign>
        </div>
        } @if (viewState === 'docu-sign' && order?.status !== orderStatus.readyToSend) {
        <aix-signatures
            [isReadOnly]="isReadOnly"
            (onSignaturesCompleted)="this.setSignaturesCompleted()"
            (onMakeChanges)="makeChanges()"
        >
        </aix-signatures>
        } @if (viewState === 'docu-sign' && order?.status !== orderStatus.readyToSend &&
        eSignWithWetSignEnabled) {
        <div class="u-mt24">
            <h5 class="ph-unmask">{{ order?.eSign?.wetSignRoles }}</h5>

            @if (!signaturesCompleted) {
            <p class="u-mt16 u-mb16 ph-unmask">
                After eSignatures have been completed, you will be prompted to collect {{
                order?.eSign?.wetSignRoles }} signatures.
            </p>
            } @if (signaturesCompleted && !eSignWithWetSignCompleted) {
            <p class="u-mt16 u-mb16 ph-unmask">
                Download the forms that require {{ order?.eSign?.wetSignRoles }} signature and
                re-upload once signatures are complete.
            </p>
            } @if (signaturesCompleted && eSignWithWetSignCompleted) {
            <p class="u-mt16 u-mb16 ph-unmask">All forms have been signed. Please continue.</p>
            } @if (signaturesCompleted) {
            <aix-form-upload #orderFormUploadPrincipal [isReadOnly]="isReadOnly"></aix-form-upload>
            }
        </div>
        } @if (viewState === 'wet-sign' || (viewState === 'docu-sign' && order?.status !==
        orderStatus.readyToSend)) {
        <div class="u-mt40">
            <aix-order-details-upload-documents
                [isReadOnly]="isReadOnly"
                [innerUpload]="true"
                [sectionClasses]="'-no-padding'"
            />
        </div>
        } @if (viewState === 'docu-sign' && order?.status !== orderStatus.readyToSend) {
        <aix-e-sign
            [isReadOnly]="isReadOnly"
            [signaturesCompleted]="signaturesCompleted && eSignWithWetSignCompleted"
            (onClickContinue)="onClickContinue()"
        ></aix-e-sign>
        } @if (viewState === 'wet-sign' && order?.status === orderStatus.readyToSend) {
        <aix-wet-sign [isReadOnly]="isReadOnly" (onClickContinue)="onClickContinue()">
        </aix-wet-sign>
        }
    </div>
</ng-template>

@if (aixTemplatesInitialized) {

<aix-expansion-panel
    [headerTemplate]="getTemplate('orderStep')"
    [headerTemplateContext]="getTemplateConfig(1, 4)"
    [isClosed]="true"
    [isDisabled]="true"
    [arrowPosition]="'none'"
>
</aix-expansion-panel>
<aix-expansion-panel
    [headerTemplate]="getTemplate('orderStep')"
    [headerTemplateContext]="getTemplateConfig(2, 4)"
    [isClosed]="true"
    [isDisabled]="true"
    [arrowPosition]="'none'"
>
</aix-expansion-panel>
<aix-expansion-panel
    [headerTemplate]="getTemplate('orderStep')"
    [headerTemplateContext]="getTemplateConfig(3, 4)"
    [isClosed]="true"
    [isDisabled]="true"
    [arrowPosition]="'none'"
>
</aix-expansion-panel>
<aix-expansion-panel
    [headerTemplate]="getTemplate('orderStep')"
    [headerTemplateContext]="getTemplateConfig(4, 4)"
    [bodyTemplate]="signaturesStepContent"
    [isClosed]="false"
    [isDisabled]="false"
    [arrowPosition]="'none'"
>
</aix-expansion-panel>
<aix-expansion-panel
    [headerTemplate]="getTemplate('orderStep')"
    [headerTemplateContext]="getTemplateConfig(5, 4)"
    [isClosed]="true"
    [isDisabled]="true"
    [arrowPosition]="'none'"
>
</aix-expansion-panel>

}
